import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import {
  CardComponent,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import onboardingPageMessages from '../../core-utils/messages/OnboardingPageMessages';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import HOMA from '../../../public/Images/Homa_White.png';
import Tick from '../../_components/atoms/Icons/Tick';
import { useNavigate } from 'react-router-dom';
import Snackbar from '../../_components/atoms/Snackbar';
import FileUploader from '../../_components/organisms/FileUploader';

export interface OnboardingPageProps {}
export interface MenuTabProps {
  isTabActive: boolean;
  isTabCompleted: boolean;
  tabName: string;
  index: number;
   
  handleTabChange: (index: number) => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = observer(({}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [completedStages, setCompletedStages] = useState<number[]>([]);

  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      height: '100vh',
      width: '100%',
    },
    item1: {
      background:
        'linear-gradient(147.58deg, #475CB0 0%, #656BC6 47.69%, #7477C8 68.03%, #A3A2D9 100%, #D5D3EB 100%, #FFFFFF 100%)',
      width: '386px',
      display: 'flex',
      flexDirection: 'column',
    },
    item2: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '3rem',
      width: '900px',
    },
    text: {
      width: '393px',
      textAlign: 'center',
    },
    logo: {
      height: '76px',
      width: '136px',
    },
    logoContainer: {
      marginTop: '3rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    fileuploadContainer: {
      display: 'flex',
      marginLeft: '-5rem',
    },
    pageTitle: {
      fontSize: '1.5rem',
      color: patientAccessTheme.palette.grey[700],
      fontWeight: 500,
    },
    pageTitleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      margin: '4rem 0 2rem 0',
    },
    tabsContainer: {
      marginTop: '1.5rem',
      alignSelf: 'flex-end',
      zIndex: 1,
    },
  });

  const { classes } = useStyles();
  const messages = onboardingPageMessages;

  const navigate = useNavigate();

  const handleHistoryPush = (path: string) => {
    navigate(path);
  };

  //snackbar
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };
  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };
  const MenuTab = ({
    isTabActive,
    isTabCompleted,
    tabName,
    index,
    handleTabChange,
  }: MenuTabProps) => {
    const useStyles = makeStyles()({
      uploadTab: {
        backgroundColor: isTabActive ? '#FFF' : '#FFFFFF4A',
        padding: '1rem 1rem',
        minWidth: '10rem',
        borderRadius: '2rem',
        width: '270px',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        cursor: 'pointer',
        position: 'relative',
      },
      tabName: {
        fontWeight: 600,
        color: isTabActive ? patientAccessTheme.palette.primary.main : '#FFF',
        textTransform: 'uppercase',
      },
      numberCirle: {
        height: '55px',
        width: '55px',
        border: isTabCompleted
          ? `4px solid #08C7B4`
          : isTabActive
          ? `4px solid #08C7B4`
          : `4px solid ${patientAccessTheme.palette.grey[300]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: isTabCompleted ? '#08C7B4' : '#FFF',
        position: 'absolute',
        right: '5px',
        fontWeight: 600,
        fontSize: '1.2rem',
        color: isTabActive ? '#08C7B4' : patientAccessTheme.palette.grey[400],
      },
    });

    const { classes } = useStyles();

    return (
      <div
        className={classes.uploadTab}
        key={tabName}
        onClick={() =>
          completedStages.includes(index) ? null : handleTabChange(index)
        }
      >
        <Typography variant="body1" className={classes.tabName}>
          {tabName}
        </Typography>
        <Typography variant="body1" className={classes.numberCirle}>
          {isTabCompleted ? <Tick /> : index + 1}
        </Typography>
      </div>
    );
  };

  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };
  const handleStageCompletion = (index: number) => {
    if (!completedStages.includes(index)) {
      setCompletedStages((prevStages) => [...prevStages, index]);
    }
    moveToNextPage(index);
  };
  const moveToNextPage = (currentIndex: number) => {
    if (currentIndex !== -1 && currentIndex < messages.UPLOAD_TABS.length - 1) {
      setActiveTab(currentIndex + 1);
    }
  };

  return (
    <Grid container className={classes.root}>
      <Grid className={classes.item1}>
        <Grid className={classes.logoContainer}>
          <img src={HOMA} alt="HOMA" className={classes.logo} />
        </Grid>
        <Grid className={classes.tabsContainer}>
          {messages.UPLOAD_TABS.map((tab, index) => (
            <MenuTab
              isTabActive={activeTab === index}
              isTabCompleted={completedStages.includes(index)}
              tabName={tab}
              index={index}
              handleTabChange={handleTabChange}
            />
          ))}
        </Grid>
      </Grid>
      <Grid className={classes.item2}>
        <Grid className={classes.pageTitleContainer}>
          <PageTitleAndSubTitle
            title={messages.TITLE}
            subTitle=""
            customButton
            customButtonText="Back to Home"
            customButtonClick={() => {
              handleHistoryPush('/home');
            }}
          />
        </Grid>

        <Grid className={classes.fileuploadContainer}>
          <CardComponent
            title={messages.FILE_UPLOADER.TITLE(
              messages.UPLOAD_TABS[activeTab],
            )}
            subTitle={messages.FILE_UPLOADER.SUBTITLE}
            body={
              <FileUploader
                activeTabIndex={activeTab}
                activeTabName={messages.UPLOAD_TABS[activeTab]}
                isFileLinkUploadSupported={false}
                fileTypeSupported={['.xlsx', '.xls', '.csv']}
                handleStageCompletion={handleStageCompletion}
                handleOpenSnackBar={handleOpenSnackBar}
              />
            }
            mainGridStyles={{
              paddingRight: '3rem',
              paddingLeft: '3rem',
              width: '100%',
            }}
          />
        </Grid>
      </Grid>
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </Grid>
  );
});

export default OnboardingPage;
